/**
 * Admin-specific functions for unit management
 * These functions require admin authentication and are used in the admin panel
 */
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';
import type { PropertyUnit } from '@/lib/units';

/**
 * Update unit availability status
 */
export async function updateUnitStatus(
  unitId: string,
  status: 'available' | 'reserved' | 'sold'
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('property_units')
      .update({ availability: status })
      .eq('id', unitId);

    if (error) {
      console.error('Error updating unit status:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_unit_status_updated', {
      unit_id: unitId,
      status,
    });

    // Sync to HubSpot (don't fail if this fails)
    try {
      const { error: syncError } = await supabase.functions.invoke(
        'sync-unit-status-to-hubspot',
        {
          body: { unitId, newStatus: status },
        }
      );

      if (syncError) {
        console.warn('HubSpot sync failed (non-blocking):', syncError);
        // Don't throw - unit update succeeded, HubSpot sync is optional
      }
    } catch (syncError) {
      console.warn('HubSpot sync error (non-blocking):', syncError);
      // Don't throw - unit update succeeded, HubSpot sync is optional
    }

    return { success: true };
  } catch (error: unknown) {
    console.error('Error updating unit status:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Update unit price
 */
export async function updateUnitPrice(
  unitId: string,
  price: number | null
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('property_units')
      .update({ price })
      .eq('id', unitId);

    if (error) {
      console.error('Error updating unit price:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_unit_price_updated', {
      unit_id: unitId,
      price: price || 0,
    });

    return { success: true };
  } catch (error: unknown) {
    console.error('Error updating unit price:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Update unit price visibility
 */
export async function updateUnitPriceVisibility(
  unitId: string,
  showPrice: boolean
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('property_units')
      .update({ show_price: showPrice })
      .eq('id', unitId);

    if (error) {
      console.error('Error updating unit price visibility:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_unit_price_visibility_updated', {
      unit_id: unitId,
      show_price: showPrice,
    });

    return { success: true };
  } catch (error: unknown) {
    console.error('Error updating unit price visibility:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Update unit view type
 */
export async function updateUnitViewType(
  unitId: string,
  viewType: 'lagoon' | 'canal'
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('property_units')
      .update({ view_type: viewType })
      .eq('id', unitId);

    if (error) {
      console.error('Error updating unit view type:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_unit_view_type_updated', {
      unit_id: unitId,
      view_type: viewType,
    });

    return { success: true };
  } catch (error: unknown) {
    console.error('Error updating unit view type:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Update multiple unit properties at once
 */
export async function updateUnit(
  unitId: string, 
  updates: {
    hubspot_deal_stage?: string | null;
    availability?: 'available' | 'reserved' | 'sold';
    price?: number | null;
    show_price?: boolean;
    is_published?: boolean;
    view_type?: 'lagoon' | 'canal';
    matterport_url?: string | null;
    kuula_url?: string | null;
    floor_plan_images?: string[];
    images?: string[];
    render_images?: string[];
    render_rotation_angle?: number;
    architecture_drawing_id?: string | null;
  }
): Promise<{ success: boolean; error?: string; data?: PropertyUnit }> {
  try {
    const { data, error } = await supabase
      .from('property_units')
      .update(updates)
      .eq('id', unitId)
      .select()
      .single();

    if (error) {
      console.error('Error updating unit:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_unit_updated', {
      unit_id: unitId,
      fields_updated: Object.keys(updates).join(','),
    });

    // Sync to HubSpot if deal stage changed AND unit is published (don't fail if this fails)
    if (updates.hubspot_deal_stage !== undefined && data.is_published !== false) {
      try {
        const { error: syncError } = await supabase.functions.invoke(
          'sync-deal-stage-to-hubspot',
          {
            body: { 
              unitId, 
              dealStage: updates.hubspot_deal_stage,
              availability: updates.availability || data.availability,
            },
          }
        );

        if (syncError) {
          console.warn('HubSpot sync failed (non-blocking):', syncError);
          // Don't throw - unit update succeeded, HubSpot sync is optional
        }
      } catch (syncError) {
        console.warn('HubSpot sync error (non-blocking):', syncError);
        // Don't throw - unit update succeeded, HubSpot sync is optional
      }
    } else if (updates.availability && data.is_published !== false) {
      // Fallback: if only availability changed (no deal stage), use old sync method
      try {
        const { error: syncError } = await supabase.functions.invoke(
          'sync-unit-status-to-hubspot',
          {
            body: { unitId, newStatus: updates.availability },
          }
        );

        if (syncError) {
          console.warn('HubSpot sync failed (non-blocking):', syncError);
          // Don't throw - unit update succeeded, HubSpot sync is optional
        }
      } catch (syncError) {
        console.warn('HubSpot sync error (non-blocking):', syncError);
        // Don't throw - unit update succeeded, HubSpot sync is optional
      }
    }

    return { success: true, data: data as PropertyUnit };
  } catch (error: unknown) {
    console.error('Error updating unit:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Update unit deal stage (and sync to HubSpot)
 */
export async function updateUnitDealStage(
  unitId: string,
  dealStage: string | null
): Promise<{ success: boolean; error?: string }> {
  try {
    // Import mapping function
    const { mapDealStageIdToStatus } = await import('@/lib/webhooks/hubspot-deal-sync');
    
    // Map deal stage to availability status
    const availability = dealStage ? mapDealStageIdToStatus(dealStage) : null;
    
    // Update both deal stage and availability
    const updates: { hubspot_deal_stage: string | null; availability?: 'available' | 'reserved' | 'sold' } = {
      hubspot_deal_stage: dealStage,
    };
    
    if (availability) {
      updates.availability = availability;
    }
    
    const result = await updateUnit(unitId, updates);
    return result;
  } catch (error: unknown) {
    console.error('Error updating unit deal stage:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Bulk update unit statuses
 */
export async function bulkUpdateStatus(
  unitIds: string[],
  status: 'available' | 'reserved' | 'sold'
): Promise<{ success: boolean; error?: string; updatedCount?: number }> {
  try {
    const { data, error } = await supabase
      .from('property_units')
      .update({ availability: status })
      .in('id', unitIds)
      .select('id');

    if (error) {
      console.error('Error bulk updating unit statuses:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_bulk_unit_status_updated' as any, {
      unit_count: unitIds.length,
      status,
    });

    // Sync each unit to HubSpot (don't fail if sync fails)
    // Run syncs in parallel but don't await - fire and forget
    unitIds.forEach((unitId) => {
      supabase.functions
        .invoke('sync-unit-status-to-hubspot', {
          body: { unitId, newStatus: status },
        })
        .catch((syncError) => {
          console.warn(`HubSpot sync failed for unit ${unitId} (non-blocking):`, syncError);
        });
    });

    return { success: true, updatedCount: data?.length || 0 };
  } catch (error: unknown) {
    console.error('Error bulk updating unit statuses:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Bulk update unit deal stages
 */
export async function bulkUpdateDealStage(
  unitIds: string[],
  dealStage: string | null
): Promise<{ success: boolean; error?: string; updatedCount?: number }> {
  try {
    // Import mapping function
    const { mapDealStageIdToStatus } = await import('@/lib/webhooks/hubspot-deal-sync');
    
    // Map deal stage to availability status
    const availability = dealStage ? mapDealStageIdToStatus(dealStage) : null;
    
    if (!availability) {
      return { success: false, error: 'Invalid deal stage' };
    }
    
    // Update both deal stage and availability
    const updates: { hubspot_deal_stage: string | null; availability: 'available' | 'reserved' | 'sold' } = {
      hubspot_deal_stage: dealStage,
      availability,
    };
    
    const { data, error } = await supabase
      .from('property_units')
      .update(updates)
      .in('id', unitIds)
      .select('id');

    if (error) {
      console.error('Error bulk updating unit deal stages:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_bulk_unit_deal_stage_updated', {
      unit_count: unitIds.length,
      dealStage,
    });

    // Sync each unit to HubSpot (don't fail if sync fails)
    unitIds.forEach((unitId) => {
      supabase.functions
        .invoke('sync-deal-stage-to-hubspot', {
          body: { unitId, dealStage, availability },
        })
        .catch((syncError) => {
          console.warn(`HubSpot sync failed for unit ${unitId} (non-blocking):`, syncError);
        });
    });

    return { success: true, updatedCount: data?.length || 0 };
  } catch (error: unknown) {
    console.error('Error bulk updating unit deal stages:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Get all units for admin (no filters, includes all availability statuses)
 */
export async function getAllUnitsForAdmin(): Promise<{
  data: PropertyUnit[] | null;
  error: string | null;
}> {
  try {
    const { data, error } = await supabase
      .from('property_units')
      .select('*')
      .order('unit_number');

    if (error) {
      console.error('Error fetching units for admin:', error);
      return { data: null, error: error.message };
    }

    return { data: data as unknown as PropertyUnit[], error: null };
  } catch (error: unknown) {
    console.error('Error fetching units for admin:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { data: null, error: errorMessage };
  }
}

